/* eslint-disable class-methods-use-this */
import defaults from 'lodash/defaults';
import differenceBy from 'lodash/differenceBy';

import debounce from '../../../../utils/debounce';
import AdUrlService from '../../../../utils/ad-url-service';

import createClustersAPI from './clusters-api';
import createMarkersAPI from './markers-api';
import createMapUtils from './maps-utils';
import createPoiAPI from './poi-api';

const POI_ZOOM_LEVEL = 16;

const LABEL_CLASSES = {
  default: 'marker',
  hover: 'marker--hover',
  active: 'marker--active',
  booked: 'marker--booked'
};
const ZOOM_CHANGED_DELAY = 4000;
const DRAGEND_DELAY = 1000;
const IDLE_DELAY = 300;
const MAX_MARKER_AMOUNT = 150;

const DEFAULT_CONFIG = () => ({
  zoom: 14,
  minZoom: 5,
  draggable: true,
  scrollwheel: true
});

const POI_MARKER_PATH =
  'https://static.spotahome.com/map/marker-point-of-interest.svg';

class MapsAPI {
  constructor(
    mapId,
    options = {},
    markerOpts = {},
    onMapDrag = () => {},
    initializePosition = true,
    deviceType = null,
    shouldUpdateMapCenter = true
  ) {
    this.options = options;
    this.initializePosition = initializePosition;
    this.mapId = mapId;
    this.gMaps = createMapUtils();
    this.onMapDrag = onMapDrag;
    this.shouldUpdateMapCenter = shouldUpdateMapCenter;
    this.onIdle = options.onIdle;
    this.mapListeners = [];

    const container = document.getElementById(mapId);
    const config = defaults(this.options, DEFAULT_CONFIG());

    this.map = this.gMaps.createMap(container, config);

    if (deviceType === 'desktop') {
      this.map.addControl(
        new window.mapboxgl.NavigationControl({ showCompass: false }),
        'bottom-left'
      );
    }

    if (this.options.showClusters) {
      this.markersAPI = createClustersAPI(this.map, {
        onMarkerClick: markerOpts.onMarkerClick,
        onMarkerMouseOver: markerOpts.onMarkerMouseOver,
        onMarkerMouseOut: markerOpts.onMarkerMouseOut,
        onClusterClick: markerOpts.onClusterClick,
        onClusterMouseOver: markerOpts.onClusterMouseOver,
        onClusterMouseOut: markerOpts.onClusterMouseOut,
        getMarkerText: markerOpts.getMarkerText
      });
    } else {
      this.markersAPI = createMarkersAPI(this.map, {
        onMarkerClick: markerOpts.onMarkerClick,
        onMarkerMouseOver: markerOpts.onMarkerMouseOver,
        onMarkerMouseOut: markerOpts.onMarkerMouseOut,
        getMarkerText: markerOpts.getMarkerText,
        labelClasses: LABEL_CLASSES,
        markerIcon: markerOpts.markerIcon
      });
    }

    this.poiAPI = createPoiAPI(this.map, {
      markerIcon: POI_MARKER_PATH,
      circleColor: '#ffccef',
      circleRadius: markerOpts.poiRadius || 1000,
      labelClasses: LABEL_CLASSES
    });

    // With trackResize (default) Mapbox auto-calls resize() on container
    // size changes and fires moveend right after — which would look like a
    // user interaction. Google Maps has no equivalent, so to keep parity
    // we skip the moveend that's paired with a resize.
    this.isResizing = false;
    this.addMapListener('resize', this.handleResize);

    // Defer listener setup: skip the initial idle/moveend events fired
    // during programmatic init (setCenter, fitBounds, addMarkers).
    // Mapbox fires moveend for every programmatic camera change, unlike
    // Google Maps' idle which only fires once everything settles.
    this.ready = false;
    this.map.once('idle', () => {
      this.ready = true;
      this.addMapListeners();
    });
  }

  addMapListeners = () => {
    this.addMapListener('dragend', this.handleDragEnd);
    this.addMapListener('zoomend', this.handleZoomChange);
    this.addMapListener('moveend', this.handleMoveEnd);
  };

  handleResize = () => {
    this.isResizing = true;
    queueMicrotask(() => {
      this.isResizing = false;
    });
  };

  handleMoveEnd = () => {
    if (!this.ready) return;
    if (this.isResizing) return;

    if (this.onIdle) {
      this.onIdle(this.getMapBounds());
    }

    if (this.options.onIdleFilter) {
      this.options.onIdleFilter(this.getFilterCoordsInBounds());
    }
  };

  onBoundsChange = () => {
    if (this.options.onZoomChange) {
      this.options.onZoomChange(this.getBounds());
    }
  };

  handleZoomChange = debounce(this.onBoundsChange, ZOOM_CHANGED_DELAY);

  handleDragEnd = debounce(() => {
    const center = this.map.getCenter();
    const lat = center.lat;
    const lng = center.lng;
    const mapCenterQueryParamValues = `${lat},${lng}`;

    if (this.shouldUpdateMapCenter) {
      AdUrlService.setQueryParameter('mapCenter', mapCenterQueryParamValues);
    }
    this.onBoundsChange();
    this.onMapDrag(mapCenterQueryParamValues);
  }, DRAGEND_DELAY);

  addMapListener = (eventName, callback) => {
    this.map.on(eventName, callback);
    this.mapListeners.push({ eventName, callback });
  };

  addPointOfInterest = pointOfInterest => {
    this.poiAPI.addPointOfInterest({
      lat: pointOfInterest.lat,
      lng: pointOfInterest.lng
    });

    const { lat, lng } = this.poiAPI.getPoiBoundaries();
    this.setCenter({ lat, lng });
    this.setZoom(POI_ZOOM_LEVEL);
  };

  removePointOfInterest = () => {
    this.poiAPI.removePointOfInterest();
  };

  updateMarkers = (prevMarkers, newMarkers, pointOfInterest) => {
    if (this.options.showClusters) {
      this.removeAllMarkers();
      this.addMarkers(newMarkers, pointOfInterest);
    } else {
      const markersToBeRemoved = differenceBy(prevMarkers, newMarkers, 'id');
      this.removeMarkers(markersToBeRemoved);
      this.addMarkers(newMarkers, pointOfInterest);
    }
  };

  getMapDimensions = (substractPadding = false) => {
    const mapDiv = document.getElementById(this.mapId);
    if (!mapDiv) {
      return false;
    }
    const padding = substractPadding ? 2 * this.options.padding : 0;
    return {
      width: mapDiv.offsetWidth - padding,
      height: mapDiv.offsetHeight - padding
    };
  };

  getMapBounds = () => {
    const bounds = this.map?.getBounds();

    if (!bounds) {
      return null;
    }

    const mapDimensions = this.getMapDimensions();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      n: ne.lat,
      s: sw.lat,
      e: ne.lng,
      w: sw.lng,
      zoom: Math.round(this.map.getZoom()),
      width: mapDimensions.width,
      height: mapDimensions.height
    };
  };

  getFilterCoordsInBounds = () => {
    const bounds = this.map.getBounds();

    if (!bounds) {
      return () => true;
    }

    const mapDimensions = this.getMapDimensions();
    const { width: mapWidth, height: mapHeight } = mapDimensions;

    return coords => {
      if (!bounds || !this.options.padding || !mapDimensions || !coords) {
        return true;
      }

      const lngLat = new window.mapboxgl.LngLat(coords.lng, coords.lat);

      if (!bounds.contains(lngLat)) {
        return false;
      }

      const point = this.map.project(lngLat);
      const xOffset = point.x;
      const yOffset = point.y;

      const inXLimits =
        xOffset - this.options.padding > 0 &&
        xOffset + this.options.padding < mapWidth;
      const inYLimits =
        yOffset - this.options.padding > 0 &&
        yOffset + this.options.padding < mapHeight;

      return inXLimits && inYLimits;
    };
  };

  addMarkers = markerList => {
    if (markerList?.length) {
      if (this.initializePosition) {
        const markerCoords = markerList.map(marker => ({
          lat: parseFloat(marker.coord[1]),
          lng: parseFloat(marker.coord[0])
        }));
        this.fitBoundsToCoords(markerCoords);
        this.initializePosition = false;
      }
      this.markersAPI.addMarkers(
        markerList,
        this.getMapDimensions(true),
        this.getBounds()
      );
    }
  };

  removeAllMarkers = () => {
    this.markersAPI.removeAllMarkers();
  };

  removeMarkers = markerList => {
    this.markersAPI.removeMarkers(markerList.map(marker => marker.id));
  };

  activeMarker = markerId => {
    this.markersAPI.setHighlightedIcon(markerId, 'active');
  };

  removeMapListeners = () => {
    this.mapListeners.forEach(({ eventName, callback }) => {
      this.map.off(eventName, callback);
    });
    this.mapListeners = [];
  };

  setCenter = opts => {
    this.map.setCenter([
      parseFloat(opts.lng),
      parseFloat(opts.lat)
    ]);
  };

  resize = () => {
    this.map?.resize();
  };

  mustCutMarkerList(markerList) {
    const tooManyMarkers = markerList.length > MAX_MARKER_AMOUNT;

    if (this.options.showClusters) {
      const currentZoom = this.map.getZoom();
      const maxZoom = this.markersAPI.getMaxZoom();
      return tooManyMarkers && currentZoom >= maxZoom;
    }
    return tooManyMarkers;
  }

  fitBoundsToMarkers = () => {
    const bounds = new window.mapboxgl.LngLatBounds();
    const markersBoundaries = this.markersAPI.getMarkersBoundaries();
    const poiBoundaries = this.poiAPI.getPoiBoundaries();

    [poiBoundaries, ...markersBoundaries].filter(Boolean).forEach(boundary => {
      bounds.extend([boundary.lng, boundary.lat]);
    });

    if (!bounds.isEmpty()) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      if (ne.lng === sw.lng && ne.lat === sw.lat) {
        bounds.extend([ne.lng + 0.01, ne.lat + 0.01]);
        bounds.extend([sw.lng - 0.01, sw.lat - 0.01]);
      }

      this.map.fitBounds(bounds, { padding: 50 });
    }
  };

  fitBoundsToCoords = (markersCoords = []) => {
    const bounds = new window.mapboxgl.LngLatBounds();
    markersCoords.forEach(coords => {
      if (coords.lat && coords.lng) {
        bounds.extend([coords.lng, coords.lat]);
      }
    });
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 50 });
    }
  };

  increaseZoom = () => {
    const currentZoom = this.map.getZoom();
    this.map.setZoom(currentZoom + 1);
  };

  setZoom = zoomValue => {
    this.map.setZoom(zoomValue);
  };

  getBounds = () => {
    const mapBounds = this.map.getBounds();
    if (!mapBounds) {
      return false;
    }
    const ne = mapBounds.getNorthEast();
    const sw = mapBounds.getSouthWest();

    return {
      northEast: { lng: ne.lng, lat: ne.lat },
      southWest: { lng: sw.lng, lat: sw.lat }
    };
  };

  updateIdleListener = onIdle => {
    this.onIdle = onIdle;
  };
}

export default MapsAPI;
